/* eslint-disable react/prop-types, react/display-name */
import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import CreatableSelect from 'react-select/creatable'

import { components } from 'react-select'

import { encodeText } from '@aytm/app/stats/lib/helpers'

import { CODEBOOKS_COLORS } from '@aytm/app/stats/page/components/lib/constans'
import { ALLOWED_HTML_TAGS, SENTIMENTS_ICON_HASH } from '@aytm/app/stats/page/comments/lib/constants'

import { stripTags, sanitizeReactSelectOption, getSearchInputOptions } from '@aytm/app/stats/page/comments/lib/utils'

import useEventListener from '@aytm/app/stats/hooks/useEventListener'
import useNonInitialEffect from '@aytm/app/stats/hooks/useNonInitialEffect'

import Popup from '@aytm/lib/rfd-ui/components/basic/popup'

// styles
import style from '@aytm/app/stats/page/components/styles/SearchInput.module.scss'

const { MenuList, MultiValue, Option, ClearIndicator, IndicatorsContainer } = components

const searchInputStyles = {
  option: (provided, state) => {
    return { ...provided, isDisabled: state.data.disabled }
  },
  multiValue: (provided, state) => {
    return { ...provided, backgroundColor: state.styles.backgroundColor }
  }
}

const filterOption = (option, searchText) => {
  const text = searchText.trim().toLowerCase()
  return (
    option.data.__isNew__ ||
    option.data.label.toLowerCase().includes(encodeText(text, false)) ||
    ('codebook' == option.data.type &&
      option.data.tags.some(tag => tag.toLowerCase().includes(encodeText(text, false))))
  )
}

const formatCreateLabel = value => `Search for: ${encodeText(value, false)}`

const isValidNewOption = value => 0 < value.trim().length

const SearchInput = ({
  placeholder,
  survey,
  tagsList,
  questionsList,
  search,
  eventName,
  isLoading,
  isProcessing,
  disabled,
  isTagsManagementDisabled,
  isSentimentsManagementDisabled,
  onSearchChange
}) => {
  const { query = [], selectedQuestion = '*' } = search

  const [searchInputValue, setSearchInputValue] = useState(
    query.map(value => ('tag' == value.type ? { ...value, value: `${value.codebook}: ${value.label}` } : value))
  )
  const [isShowSearchIconOverlay, setIsShowSearchIconOverlay] = useState(false)

  const [searchInputOptions, setSearchInputOptions] = useState(
    () =>
      getSearchInputOptions(
        searchInputValue,
        survey,
        tagsList,
        questionsList,
        selectedQuestion,
        isTagsManagementDisabled,
        isSentimentsManagementDisabled
      ) || []
  )

  const menuTimer = useRef(null)

  const searchInputRef = useRef(null)
  const searchInputValueRef = useRef('')

  const convertSearchInputValue = useCallback(
    () =>
      searchInputValue.map(value => {
        if ('codebook' == value.type) {
          Object.assign(value, {
            color: `#${survey.codeBooks.find(codebook => codebook.title == value.value)?.color || '88d1cd'}`
          })
        }
        if ('tag' == value.type) {
          Object.assign(value, {
            color: `#${
              survey.codeBooks.find(codebook => codebook.tags.some(tag => `${codebook.title}: ${tag}` == value.value))
                ?.color || '88d1cd'
            }`
          })
        }
        return value
      }) || [],
    [searchInputValue, survey.codeBooks]
  )

  const onChange = useCallback(
    (newValue, actionMeta) => {
      if ('clear' == actionMeta.action) {
        setSearchInputValue([])
        onSearchChange({ query: [] })
        return
      }

      switch (actionMeta.action) {
        case 'pop-value':
        case 'remove-value': {
          setSearchInputValue(newValue || [])
          break
        }
        case 'select-option': {
          setSearchInputValue(
            newValue.map(value => {
              if ('codebook' == value.type) {
                return { type: 'codebook', label: value.label, value: value.value }
              }
              if ('tag' == value.type) {
                return { type: 'tag', label: value.label, value: value.value, codebook: value.codebook }
              }
              return value
            }) || []
          )
          break
        }
        case 'create-option':
        default: {
          const newOption = sanitizeReactSelectOption(newValue[newValue.length - 1])
          newOption.type = 'plain'
          if (!searchInputValue.some(item => item.value == newOption.value && 'plain' == newOption.type)) {
            setSearchInputValue([...newValue])
          }
          break
        }
      }
      searchInputValueRef.current = ''
    },
    [searchInputValue, onSearchChange]
  )

  const onMenuOpen = () => {
    if (searchInputRef.current?.state.inputValue) {
      setIsShowSearchIconOverlay(true)
    }
  }

  const onMenuClose = useCallback(() => {
    if (searchInputRef.current.state.inputValue) {
      searchInputValueRef.current = searchInputRef.current.state.inputValue
    }

    menuTimer.current && clearTimeout(menuTimer.current)
    menuTimer.current = setTimeout(() => setIsShowSearchIconOverlay(false), 100)

    return () => menuTimer.current && clearTimeout(menuTimer.current)
  }, [])

  const onKeyDown = useCallback(
    event => {
      if (event && 'Enter' === event.key && !searchInputRef.current?.state?.menuIsOpen) {
        onSearchChange({
          query: searchInputValue.map(value =>
            'tag' == value.type ? { ...value, value: value.value.replace(`${value.codebook}: `, '') } : value
          )
        })
      }
    },
    [searchInputValue, onSearchChange]
  )

  const onSearchInputChange = useCallback(
    (value = '') => {
      // tricky way to add "search by response UID option
      const updatedOptions = searchInputOptions.filter(opt => !['uid'].includes(opt.type))
      if (value.trim() && !isNaN(value)) {
        const uidOption = {
          type: 'uid',
          label: value,
          value: parseInt(value, 10)
        }
        updatedOptions.unshift(uidOption)
      }
      setSearchInputOptions(updatedOptions)
    },
    [searchInputOptions]
  )

  const onSearchInputIconClick = useCallback(() => {
    searchInputRef.current.blur()
    searchInputRef.current.state.menuIsOpen = false
    searchInputRef.current.state.isFocused = false

    onSearchChange({
      query: searchInputValue.map(value =>
        'tag' == value.type ? { ...value, value: value.value.replace(`${value.codebook}: `, '') } : value
      )
    })
  }, [searchInputValue, onSearchChange])

  const onSearchOverlayClick = useCallback(() => {
    if (
      searchInputValueRef.current &&
      (!searchInputValue ||
        (searchInputValue &&
          !searchInputValue.some(item => item.value === searchInputValueRef.current && 'plain' === item.type)))
    ) {
      onSearchChange({
        query: [
          ...searchInputValue.map(value =>
            'tag' == value.type ? { ...value, value: value.value.replace(`${value.codebook}: `, '') } : value
          ),
          { type: 'plain', label: searchInputValueRef.current, value: searchInputValueRef.current }
        ]
      })
      searchInputValueRef.current = ''
    }
    setIsShowSearchIconOverlay(false)
  }, [searchInputValue, onSearchChange])

  const showClearIndicator = useCallback(
    props => {
      const inputValue = props?.getValue() || []
      const difference = query
        .filter(
          _query =>
            !inputValue.some(_inputValue =>
              'tag' == _query.type && 'tag' == _inputValue.type
                ? `${_query.codebook}: ${_query.value}` == _inputValue.value
                : _query.type == _inputValue.type && _query.value == _inputValue.value
            )
        )
        .concat(
          inputValue.filter(
            _inputValue =>
              !query.some(_query =>
                'tag' == _query.type && 'tag' == _inputValue.type
                  ? `${_query.codebook}: ${_query.value}` == _inputValue.value
                  : _query.type == _inputValue.type && _query.value == _inputValue.value
              )
          )
        )

      return !difference.length && !searchInputRef.current?.state.inputValue
    },
    [query]
  )

  const searchInputComponents = useMemo(() => {
    return {
      DropdownIndicator: null,
      MenuList: ({ children, ...props }) =>
        children.length ? <MenuList {...props}>{children}</MenuList> : <MenuList className="is-hidden" {...props} />,
      MultiValue: ({ children, data, ...props }) => {
        const iconColor = data.color || '#444444'
        const backgroundColor =
          'batch' != data.type && data.color
            ? CODEBOOKS_COLORS.some(color => color[0].toLowerCase() == data.color.toLowerCase())
              ? CODEBOOKS_COLORS.find(color => color[0].toLowerCase() == data.color.toLowerCase())[1].toLowerCase()
              : `${data.color}90`
            : '#efefef'

        const renderValue = () => (
          <MultiValue data={data} styles={{ backgroundColor }} {...props}>
            {'codebook' == data.type && <i className="icon-aytmfd-folder-closed" style={{ color: iconColor }} />}
            {'tag' == data.type && <i className="icon-aytmfd-tag" style={{ color: iconColor }} />}
            {'sentiment' == data.type && (
              <i className={`icon-aytmfd-sentiment-${SENTIMENTS_ICON_HASH[data.value]}`} style={{ color: iconColor }} />
            )}
            {'uid' == data.type && <i className="icon-aytmfd-singleuser" style={{ color: iconColor }} />}
            {'cluster' == data.type && <i className="icon-aytmfd-heatmap-cluster" style={{ color: iconColor }} />}
            {'batch' == data.type && <i className="icon-aytmfd-map-view" style={{ color: iconColor }} />}
            {'batch' == data.type && data.label && ALLOWED_HTML_TAGS.test(data.label) ? (
              <span dangerouslySetInnerHTML={{ __html: stripTags(children, 'b', 'i', 'u') }} />
            ) : (
              <span dangerouslySetInnerHTML={{ __html: children }} />
            )}
          </MultiValue>
        )

        return 'tag' == data.type ? (
          <Popup
            className="rfd-ui-popup-mini"
            trigger="hover"
            direction="top"
            portal
            content={() => (
              <span
                dangerouslySetInnerHTML={{
                  __html: `<b>${data.codebook}</b> codebook`
                }}
              />
            )}
          >
            {renderValue()}
          </Popup>
        ) : (
          renderValue()
        )
      },
      Option: ({ children, data, ...props }) => (
        <Option data={data} {...props}>
          <span
            className={classNames(style['search-input-value'], {
              [style['search-input-sub-value']]: Object.hasOwn(data, 'codebook'),
              [style['search-input-uid']]: 'uid' == data.type
            })}
          >
            {'codebook' == data.type && <i className="icon-aytmfd-folder-closed" style={{ color: data.color }} />}
            {'tag' == data.type && <i className="icon-aytmfd-tag" style={{ color: data.color }} />}
            {'sentiment' == data.type && <i className={`icon-aytmfd-sentiment-${SENTIMENTS_ICON_HASH[data.value]}`} />}
            {'uid' == data.type && <i className="icon-aytmfd-singleuser" />}
            {'cluster' == data.type && <i className="icon-aytmfd-heatmap-cluster" />}
            {'batch' == data.type && <i className="icon-aytmfd-map-view" style={{ color: data.color }} />}
            {'batch' == data.type && data.label && ALLOWED_HTML_TAGS.test(data.label) ? (
              <span dangerouslySetInnerHTML={{ __html: stripTags(children, 'b', 'i', 'u') }} />
            ) : (
              <span dangerouslySetInnerHTML={{ __html: children }} />
            )}
          </span>
        </Option>
      ),
      NoOptionsMessage: () => null,
      IndicatorsContainer: props => (
        <IndicatorsContainer {...props}>
          {props?.isDisabled || (props?.hasValue && showClearIndicator(props)) ? null : (
            <i
              className={`${style['search-icon']} search-input__indicator icon-aytmfd-search`}
              onClick={onSearchInputIconClick}
            />
          )}
          {props?.children}
        </IndicatorsContainer>
      ),
      ClearIndicator: props => (showClearIndicator(props) ? <ClearIndicator {...props} /> : null)
    }
  }, [showClearIndicator, onSearchInputIconClick])

  useEffect(
    () => onSearchInputChange(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  useNonInitialEffect(
    () =>
      setSearchInputValue(
        query.map(value => ('tag' == value.type ? { ...value, value: `${value.codebook}: ${value.label}` } : value))
      ),
    [query, isLoading]
  )

  useNonInitialEffect(
    () =>
      setSearchInputOptions(
        getSearchInputOptions(
          searchInputValue,
          survey,
          tagsList,
          questionsList,
          selectedQuestion,
          isTagsManagementDisabled,
          isSentimentsManagementDisabled
        ) || []
      ),
    [
      searchInputValue,
      survey,
      tagsList,
      questionsList,
      selectedQuestion,
      isTagsManagementDisabled,
      isSentimentsManagementDisabled
    ]
  )

  useEventListener(eventName, event => {
    if (isLoading || isProcessing || disabled) return

    if (event.detail) {
      const { item } = event.detail
      if (
        searchInputValue.some(
          v => v.value == (Object.hasOwn(item, 'codebook') && !Object.hasOwn(item, 'tag') ? item.codebook : item.value)
        )
      ) {
        return
      }

      setSearchInputValue(
        searchInputValue.concat([
          Object.hasOwn(item, 'codebook') && !Object.hasOwn(item, 'tag')
            ? { type: 'codebook', label: item.label, value: item.codebook }
            : Object.hasOwn(item, 'codebook') && Object.hasOwn(item, 'tag') && !Object.hasOwn(item, 'sentiment')
            ? { type: 'tag', label: item.label, value: item.value, codebook: item.codebook }
            : Object.hasOwn(item, 'codebook') && Object.hasOwn(item, 'tag') && Object.hasOwn(item, 'sentiment')
            ? { type: 'sentiment', label: `sentiment: ${item.label}`, value: item.value, sentiment: item.sentiment }
            : {}
        ])
      )
    }
  })

  return (
    <div className={style['search-input-container']} data-testid="stat-comments-search">
      <CreatableSelect
        ref={searchInputRef}
        className="search-input"
        classNamePrefix="search-input"
        components={searchInputComponents}
        styles={searchInputStyles}
        placeholder={placeholder || 'Search...'}
        options={searchInputOptions}
        value={convertSearchInputValue()}
        createOptionPosition="first"
        noResultsText={null}
        openMenuOnClick={true}
        openMenuOnFocus={false}
        cacheOptions
        isMulti
        isClearable
        isSearchable
        isLoading={isLoading || isProcessing}
        isDisabled={isLoading || isProcessing || disabled}
        isOptionDisabled={option => option.disabled}
        isValidNewOption={isValidNewOption}
        onMenuOpen={onMenuOpen}
        onMenuClose={onMenuClose}
        onKeyDown={onKeyDown}
        onChange={onChange}
        onInputChange={onSearchInputChange}
        filterOption={filterOption}
        formatCreateLabel={formatCreateLabel}
      />
      {isShowSearchIconOverlay && <div className={style['search-icon-overlay']} onClick={onSearchOverlayClick} />}
    </div>
  )
}

SearchInput.propTypes = {
  placeholder: PropTypes.string,
  survey: PropTypes.object,
  tagsList: PropTypes.array,
  search: PropTypes.object,
  eventName: PropTypes.string,
  isLoading: PropTypes.bool,
  isProcessing: PropTypes.bool,
  disabled: PropTypes.bool,
  isTagsManagementDisabled: PropTypes.bool,
  isSentimentsManagementDisabled: PropTypes.bool,
  onSearchChange: PropTypes.func
}

SearchInput.displayName = 'SearchInput'

export default SearchInput
